import React, { useCallback, useEffect, useState } from 'react';
import { FilePlus2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

export const NewDiagramButton: React.FC = () => {
  const navigate = useNavigate();
  const { hasUnsavedChanges } = useUnsavedChanges();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const createNewDiagram = useCallback(() => {
    setShowConfirmation(false);
    // The user is intentionally starting over. Do not carry the current
    // diagram's crash-recovery snapshot into the newly generated diagram ID.
    localStorage.removeItem('flowi-autosave');
    localStorage.removeItem('flowi-autosave-timestamp');
    localStorage.removeItem('flowi-autosave-diagram-id');
    navigate('/editor');
  }, [navigate]);

  const requestNewDiagram = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmation(true);
      return;
    }

    createNewDiagram();
  }, [createNewDiagram, hasUnsavedChanges]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        requestNewDiagram();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [requestNewDiagram]);

  return (
    <>
      <button
        type="button"
        onClick={requestNewDiagram}
        className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
        title="Create new diagram (Ctrl+N)"
      >
        <FilePlus2 className="h-4 w-4" />
        <span className="hidden lg:inline">New</span>
      </button>

      <ConfirmDialog
        open={showConfirmation}
        title="Create a new diagram?"
        description="Your unsaved changes in this diagram will be discarded. Save your work first if you want to keep it."
        confirmLabel="Create new"
        cancelLabel="Keep editing"
        destructive
        onCancel={() => setShowConfirmation(false)}
        onConfirm={createNewDiagram}
      />
    </>
  );
};
